import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { PageContainerWrapper, TablePlus } from '../../../../components';
import useTable from '../../../../hooks/useTable.hook';
import {
  Columns,
  getRefundsListService,
} from '../../../../services/index.service';
import RefundsAndDeductionsFilterForm from './RefundsAndDeductionsFilterForm';
import RefundDeductionDetailModal from './RefundDeductionDetailModal';

function RefundsAndDeductionsList() {
  const { t } = useTranslation();
  const breadcrumb = Columns.refundsAndDeductionsBreadcrumb();

  const [globalFilter, setGlobalFilter] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Default to all records (without default date filter, matching Customer Outstanding Details UX)
  const defaultFilter = {
    startDate: '',
    endDate: '',
    dateType: undefined,
    paymentType: '',
    deductionStatus: '',
  };

  const [filterValue, setFilter] = useState(defaultFilter);
  const [values, setValues] = useState({ ...defaultFilter });

  const extraQuery = useMemo(() => {
    const q = { ...filterValue };
    delete q.dateType;
    Object.keys(q).forEach((key) => {
      if (q[key] === '' || q[key] === undefined || q[key] === null) {
        delete q[key];
      }
    });
    return q;
  }, [filterValue]);

  const [tableHandler, tableProps] = useTable(
    getRefundsListService,
    extraQuery,
    false,
    false,
    false,
    false
  );

  const {
    data,
    isLoading,
    count,
    page,
    noOfPages,
    sizePerPage,
    filter,
    sortBy,
    sortType,
  } = tableProps;

  const { onFilterChange, onFilterResetChange, onExport, onTableSort } =
    tableHandler;

  const handleAction = (actionType, row) => {
    if (actionType === 'view') {
      setSelectedRefund(row);
      setIsDetailModalOpen(true);
    }
  };

  const columns = Columns.refundsAndDeductionsColumn({
    page,
    sizePerPage,
    onAction: handleAction,
    onSort: onTableSort,
    onFilterChange,
    filter,
  });

  const onFilterSubmit = (vals) => {
    const updatedValues = { ...values, ...vals };
    setFilter(updatedValues);
    setValues(updatedValues);

    onFilterChange({
      startDate: vals?.startDate || null,
      endDate: vals?.endDate || null,
      paymentType: vals?.paymentType || null,
      deductionStatus: vals?.deductionStatus || null,
    });
    setGlobalFilter(false);
  };

  const onReset = () => {
    setFilter(defaultFilter);
    setValues({ ...defaultFilter });
    onFilterChange({
      startDate: null,
      endDate: null,
      paymentType: null,
      deductionStatus: null,
    });
    onFilterResetChange();
    setGlobalFilter(false);
  };

  const onTableResetFilter = () => {
    onReset();
  };

  const headerBtn = {
    add: {
      name: (
        <>
          <em className="icon ni ni-filter-fill me-1" /> Filter
        </>
      ),
      handler: () => {
        setGlobalFilter(!globalFilter);
      },
    },
    csvExport: {
      handler: () => onExport('', columns),
    },
  };

  return (
    <>
      <PageContainerWrapper
        heading={t('text.report.refundsAndDeductionsHeading')}
        breadcrumb={breadcrumb}
        btn={headerBtn}
        extraClass="btn btn-primary d-block d-xl-none"
      >
        {/* Filters Section */}
        <div className="globalFilters mb-3">
          <div className="card">
            <div className="card-inner">
              <div
                className={`globalFilters_inner ${
                  globalFilter ? 'filterToggle' : ''
                }`}
              >
                <div className="mb-3 d-block d-xl-none">
                  <Link to="#" onClick={() => setGlobalFilter(false)}>
                    <em className="icon ni ni-arrow-long-left me-1" />
                    {t('text.common.back')}
                  </Link>
                </div>
                <RefundsAndDeductionsFilterForm
                  onSubmit={onFilterSubmit}
                  onReset={onReset}
                  filterValue={filterValue}
                  setValues={setValues}
                  values={values}
                />
              </div>
              <div
                className={`${globalFilter ? 'toggle-overlay' : ''}`}
                onClick={() => setGlobalFilter(false)}
              />
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="tableScrollHide">
          <TablePlus
            hasLimit
            noOfPage={noOfPages}
            sizePerPage={sizePerPage}
            page={page}
            count={count}
            tableData={data}
            tableColumns={columns}
            statusAction={false}
            showAction={false}
            tableLoader={isLoading}
            onFilterResetChange={onTableResetFilter}
            filter={filter}
            sortBy={sortBy}
            sortType={sortType}
            scrollTopPosition
          />
        </div>
      </PageContainerWrapper>

      {/* Detail Modal */}
      <RefundDeductionDetailModal
        show={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false);
          setSelectedRefund(null);
        }}
        data={selectedRefund}
      />
    </>
  );
}

export default RefundsAndDeductionsList;
